<template>
  <div class="ledger-page">
    <b-card class="rounded-lg">
      <div class="d-flex justify-content-between align-items-start flex-wrap mb-1">
        <div>
          <h4 class="mb-50">
            {{ $t('ledger_maps_title') }}
          </h4>
          <p class="text-muted mb-0">
            {{ $t('ledger_maps_hint') }}
          </p>
        </div>
        <div class="d-flex flex-wrap">
          <b-button
            size="sm"
            variant="outline-primary"
            class="mr-50 mb-50"
            :to="{ name: 'ledger-maps-movements' }"
          >
            {{ $t('ledger_maps_movements') }}
          </b-button>
          <b-button
            v-if="canOpenGis"
            size="sm"
            variant="primary"
            class="mb-50"
            :to="gisRoute"
          >
            {{ $t('ledger_maps_add_from_gis') }}
          </b-button>
        </div>
      </div>

      <b-row class="mb-1">
        <b-col
          cols="12"
          md="5"
        >
          <b-form-input
            v-model="keyword"
            :placeholder="$t('ledger_search')"
            @keyup.enter="loadItems"
          />
        </b-col>
        <b-col
          cols="12"
          md="4"
          class="d-flex align-items-center mt-50 mt-md-0"
        >
          <b-form-checkbox v-model="inStockOnly">
            {{ $t('ledger_maps_in_stock_only') }}
          </b-form-checkbox>
        </b-col>
      </b-row>

      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>
      <div
        v-else-if="!rows.length"
        class="text-center text-muted py-4"
      >
        {{ $t('ledger_maps_empty') }}
      </div>
      <div v-else>
        <div class="d-none d-md-block">
          <b-table
            :items="rows"
            :fields="fields"
            small
            hover
            responsive
            class="ledger-dense-table"
          >
            <template #cell(map_number)="data">
              <b-link :to="showRoute(data.item)">
                {{ data.item.map_number || '—' }}
              </b-link>
            </template>
            <template #cell(quadrant)="data">
              {{ quadrant(data.item) }}
            </template>
            <template #cell(qty_on_hand)="data">
              <span class="ledger-num">{{ qty(data.item.qty_on_hand) }}</span>
            </template>
            <template #cell(unit_cost)="data">
              <span class="ledger-num">{{ money(data.item.unit_cost) }}</span>
            </template>
            <template #cell(inventory_value)="data">
              <span class="ledger-num">{{ money(data.item.inventory_value) }}</span>
            </template>
          </b-table>
        </div>
        <div class="d-md-none">
          <article
            v-for="item in rows"
            :key="item.id"
            class="ledger-map-card"
            @click="$router.push(showRoute(item))"
          >
            <strong>{{ item.map_number || displayName(item) }}</strong>
            <div class="text-muted">
              {{ quadrant(item) }}
            </div>
            <div class="ledger-num small mt-50">
              {{ $t('ledger_maps_qty') }} {{ qty(item.qty_on_hand) }}
              · {{ money(item.unit_cost) }}
            </div>
            <div class="ledger-num">
              {{ $t('ledger_maps_value') }} {{ money(item.inventory_value) }}
            </div>
          </article>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BFormCheckbox, BButton, BTable, BLink, BSpinner,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getMapStock } from '@/api/accounting/mapInventory'
import {
  listFrom, formatLedgerMoney, formatLedgerQty, apiErrorMessage,
} from '@/utils/ledger'

export default {
  name: 'MapStockList',
  components: {
    BCard, BRow, BCol, BFormInput, BFormCheckbox, BButton, BTable, BLink, BSpinner,
  },
  data() {
    return {
      loading: false,
      keyword: '',
      inStockOnly: false,
      rows: [],
    }
  },
  computed: {
    canOpenGis() {
      return this.hasPermission('view seizure_land_inventories')
    },
    gisRoute() {
      return { name: 'seizure-land-inventories-list', query: { tab: 'maps' } }
    },
    fields() {
      return [
        { key: 'map_number', label: this.$t('ledger_maps_number') },
        { key: 'quadrant', label: this.$t('ledger_maps_ne') },
        { key: 'qty_on_hand', label: this.$t('ledger_maps_qty') },
        { key: 'unit_cost', label: this.$t('ledger_maps_unit_cost') },
        { key: 'inventory_value', label: this.$t('ledger_maps_value') },
      ]
    },
  },
  watch: {
    inStockOnly: 'loadItems',
  },
  mounted() {
    this.loadItems()
  },
  methods: {
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    qty(value) {
      return formatLedgerQty(value || 0, this.$i18n.locale)
    },
    axis(value, kind) {
      const key = `ledger_maps_${kind}_${value}`
      return value && this.$te(key) ? this.$t(key) : (value || '')
    },
    quadrant(item) {
      if (!item) return '—'
      const ns = this.axis(item.north_south, 'ns')
      const ew = this.axis(item.east_west, 'ew')
      return [ns, ew].filter(Boolean).join(' / ') || '—'
    },
    displayName(item) {
      return (item && item.display_name) || this.quadrant(item)
    },
    showRoute(item) {
      return { name: 'ledger-maps-view', params: { id: item.id } }
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon',
          variant,
        },
      })
    },
    async loadItems() {
      this.loading = true
      try {
        const params = {}
        const keyword = String(this.keyword || '').trim()
        if (keyword) params.keyword = keyword
        if (this.inStockOnly) params.in_stock_only = 1
        const res = await getMapStock(params)
        this.rows = listFrom(res)
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.ledger-num {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.ledger-map-card {
  border-bottom: 1px solid #eee;
  padding: 0.85rem 0.15rem;
  cursor: pointer;
}
</style>
